/**
 * Access Control Configuration
 *
 * Hierarchical access levels from highest to lowest privilege.
 * Users with higher access levels automatically have access to lower levels.
 */

import { type RouteKey, ROUTES } from './routes';

export const AccessLevel = {
  /** System administrator - full access to everything */
  SYSTEM_ADMIN: 0,

  /** Admin of the current auction house */
  AUCTION_HOUSE_ADMIN: 1,

  /** Worker/staff member of the current auction house */
  AUCTION_HOUSE_WORKER: 2,

  /** Staff of any auction house - for context-free admin pages like /admin selector */
  AUCTION_HOUSE_STAFF: 3,

  /** User with bidder privileges in current auction */
  CURRENT_BIDDER: 4,

  /** Registered bidder (not in current auction) */
  BIDDER: 5,

  /** Any authenticated user */
  AUTHENTICATED: 6,

  /** Public access - anyone including visitors */
  PUBLIC: 7,
} as const;

export type AccessLevelValue = typeof AccessLevel[keyof typeof AccessLevel];

/**
 * Route access configuration
 * TypeScript will error if any route from ROUTES is missing!
 */
export const routeAccess: Record<RouteKey, AccessLevelValue> = {
  // Public routes
  [ROUTES.HOME]: AccessLevel.PUBLIC,
  [ROUTES.SIGN_UP]: AccessLevel.PUBLIC,
  [ROUTES.LOGIN]: AccessLevel.PUBLIC,
  [ROUTES.LOGOUT]: AccessLevel.PUBLIC,
  [ROUTES.AUTH_CALLBACK]: AccessLevel.PUBLIC,
  [ROUTES.FORGOT_PASSWORD]: AccessLevel.PUBLIC,
  [ROUTES.RESET_PASSWORD]: AccessLevel.PUBLIC,
  [ROUTES.WELCOME]: AccessLevel.AUTHENTICATED,
  [ROUTES.AUCTIONS]: AccessLevel.PUBLIC,
  [ROUTES.AUCTION_HOUSE]: AccessLevel.PUBLIC,
  [ROUTES.CATALOG]: AccessLevel.PUBLIC,
  [ROUTES.ITEM]: AccessLevel.PUBLIC,
  [ROUTES.PAGES]: AccessLevel.PUBLIC,
  [ROUTES.SETTINGS]: AccessLevel.AUTHENTICATED,

  // Admin routes
  [ROUTES.ADMIN]: AccessLevel.AUCTION_HOUSE_STAFF,
  [ROUTES.ADMIN_ERROR_DEMO]: AccessLevel.SYSTEM_ADMIN,
  [ROUTES.ADMIN_ASSIGN_ADMINS]: AccessLevel.SYSTEM_ADMIN,
  [ROUTES.ADMIN_SALE_IMPORTS]: AccessLevel.SYSTEM_ADMIN,
  [ROUTES.ADMIN_AUCTION_HOUSES]: AccessLevel.SYSTEM_ADMIN,
  [ROUTES.ADMIN_EMAIL_TEMPLATES]: AccessLevel.SYSTEM_ADMIN,
  [ROUTES.ADMIN_EMAIL_LOGS]: AccessLevel.SYSTEM_ADMIN,
  [ROUTES.ADMIN_SETTINGS]: AccessLevel.AUCTION_HOUSE_WORKER,
  [ROUTES.ADMIN_CATALOGS]: AccessLevel.AUCTION_HOUSE_WORKER,
  [ROUTES.ADMIN_NEW_CATALOG]: AccessLevel.AUCTION_HOUSE_WORKER,
  [ROUTES.ADMIN_CATALOG_DETAIL]: AccessLevel.AUCTION_HOUSE_WORKER,
  [ROUTES.ADMIN_CATALOG_ITEM]: AccessLevel.AUCTION_HOUSE_WORKER,
  [ROUTES.ADMIN_ITEMS]: AccessLevel.AUCTION_HOUSE_WORKER,
  [ROUTES.ADMIN_ITEM_DETAIL]: AccessLevel.AUCTION_HOUSE_WORKER,
  [ROUTES.ADMIN_INSIGHTS]: AccessLevel.AUCTION_HOUSE_WORKER,
  [ROUTES.ADMIN_INSIGHTS_UPGRADE]: AccessLevel.SYSTEM_ADMIN,
  [ROUTES.ADMIN_INSIGHTS_UNDERBIDDERS]: AccessLevel.AUCTION_HOUSE_WORKER,
  [ROUTES.ADMIN_INSIGHTS_TOP_REVENUE]: AccessLevel.AUCTION_HOUSE_WORKER,
  [ROUTES.ADMIN_CUSTOMERS]: AccessLevel.AUCTION_HOUSE_WORKER,
  [ROUTES.ADMIN_BIDDERS]: AccessLevel.AUCTION_HOUSE_WORKER,

  // Live auction routes
  [ROUTES.LIVE_AUCTION]: AccessLevel.PUBLIC,
} as const;

/**
 * Check if user has required access level
 * Returns true if user's access level is >= required level (lower number = higher access)
 */
export const hasAccess = (
  userAccessLevel: AccessLevelValue,
  requiredLevel: AccessLevelValue
): boolean => {
  return userAccessLevel <= requiredLevel;
};

/**
 * Get user's access level based on their roles for current auction house
 */
export const getUserAccessLevel = (
  userRoles: Array<{ role: string; auctionHouseId?: string | null }>,
  currentAuctionHouseId?: string
): AccessLevelValue => {
  // Check for system admin
  if (userRoles.some(r => r.role === 'SYSTEM_ADMIN')) {
    return AccessLevel.SYSTEM_ADMIN;
  }

  // If no auction house context, check if user has ANY admin/worker role
  if (!currentAuctionHouseId) {
    const hasAnyStaffRole = userRoles.some(
      r => r.role === 'AUCTION_HOUSE_ADMIN' || r.role === 'AUCTION_HOUSE_WORKER'
    );
    if (hasAnyStaffRole) {
      return AccessLevel.AUCTION_HOUSE_STAFF;
    }
    return AccessLevel.AUTHENTICATED;
  }

  // Check for auction house specific roles
  const auctionHouseRoles = userRoles.filter(
    r => r.auctionHouseId === currentAuctionHouseId
  );

  if (auctionHouseRoles.some(r => r.role === 'AUCTION_HOUSE_ADMIN')) {
    return AccessLevel.AUCTION_HOUSE_ADMIN;
  }

  if (auctionHouseRoles.some(r => r.role === 'AUCTION_HOUSE_WORKER')) {
    return AccessLevel.AUCTION_HOUSE_WORKER;
  }

  // If user has roles (even if not for this auction house), they're authenticated
  // Only return PUBLIC for users with no roles at all
  return userRoles.length > 0 ? AccessLevel.AUTHENTICATED : AccessLevel.PUBLIC;
};
